// src/components/dashboard/settings/Users.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";

// Core Stable MUI Icons
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const Users = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Clinical User Accounts Dataset
  const [userList, setUserList] = useState([
    {
      id: "USR-101",
      name: "Dr. Usman",
      email: "usman.lead@neuroez.com",
      role: "Lead Neurologist",
      npi: "1982736450",
      status: "Active",
      lastLogin: "09/15/2026 04:32 PM",
    },
    {
      id: "USR-102",
      name: "Dr. Sarah Jenkins",
      email: "s.jenkins@neuroez.com",
      role: "Neuropsychiatrist",
      npi: "1284920194",
      status: "Active",
      lastLogin: "09/15/2026 02:15 PM",
    },
    {
      id: "USR-103",
      name: "Elena Rostova",
      email: "elena.rn@neuroez.com",
      role: "Registered Nurse (RN)",
      npi: "1093847291",
      status: "Active",
      lastLogin: "09/14/2026 06:10 PM",
    },
    {
      id: "USR-104",
      name: "Marcus Aurelius",
      email: "m.aurelius@neuroez.com",
      role: "Physical Therapist",
      npi: "1827394851",
      status: "Active",
      lastLogin: "09/12/2026 11:20 AM",
    },
    {
      id: "USR-105",
      name: "Awais Admin",
      email: "awais@gmail.com",
      role: "Super Admin",
      npi: "N/A",
      status: "Active",
      lastLogin: "09/15/2026 05:01 PM",
    },
    {
      id: "USR-106",
      name: "Rachel Zane",
      email: "rachel.z@neuroez.com",
      role: "Billing Specialist",
      npi: "N/A",
      status: "Inactive",
      lastLogin: "08/30/2026 10:00 AM",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);

  // New User Form State
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    role: "Physician",
    npi: "",
  });

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const roleOptions = [
    "All",
    "Lead Neurologist",
    "Neuropsychiatrist",
    "Registered Nurse (RN)",
    "Physical Therapist",
    "Super Admin",
    "Billing Specialist",
  ];

  const handleDelete = (id) => {
    setUserList((prev) => prev.filter((u) => u.id !== id));
  };

  const handleCreateUser = () => {
    if (!newUserData.name.trim() || !newUserData.email.trim()) return;
    const newEntry = {
      id: `USR-${Math.floor(100 + Math.random() * 900)}`,
      name: newUserData.name,
      email: newUserData.email,
      role: newUserData.role,
      npi: newUserData.npi.trim() || "N/A",
      status: "Active",
      lastLogin: "Never",
    };
    setUserList([newEntry, ...userList]);
    setNewUserData({ name: "", email: "", role: "Physician", npi: "" });
    setOpenModal(false);
  };

  const filteredUsers = userList.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === "All" || user.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    if (role.includes("Neurologist") || role.includes("Doctor") || role.includes("Physician")) {
      return { bg: pastelTealBg, color: tealButton };
    }
    if (role.includes("Admin")) {
      return { bg: isDark ? "rgba(168, 85, 247, 0.15)" : "#FAF5FF", color: isDark ? "#C084FC" : "#9333EA" };
    }
    if (role.includes("Nurse") || role.includes("Therapist")) {
      return { bg: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7", color: isDark ? "#34D399" : "#15803D" };
    }
    return { bg: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9", color: secondaryTextColor };
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & ACTION BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PeopleAltRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                User Management
              </Typography>
              <Chip
                label="Clinical Personnel"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Control practitioner logins, security privileges, and provider credentials
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            height: "40px",
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add User
        </Button>
      </Box>

      {/* 2. CONTROLLER FILTER BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "18px 24px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search practitioner by name, email, or user id..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: secondaryTextColor, fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { sm: "400px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              backgroundColor: controlBg,
              fontSize: "0.85rem",
              color: primaryTextColor,
              "& fieldset": { borderColor: controlBorder },
              "&:hover fieldset": { borderColor: tealMain },
              "&.Mui-focused fieldset": { borderColor: tealMain },
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, width: { xs: "100%", sm: "auto" } }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, whiteSpace: "nowrap" }}>
            Filter Role:
          </Typography>
          <Select
            size="small"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            IconComponent={KeyboardArrowDownRoundedIcon}
            MenuProps={menuProps}
            sx={{
              minWidth: "180px",
              height: "40px",
              borderRadius: "6px",
              backgroundColor: controlBg,
              fontSize: "0.85rem",
              color: primaryTextColor,
              "& fieldset": { borderColor: controlBorder },
              "&:hover fieldset": { borderColor: tealMain },
              "&.Mui-focused fieldset": { borderColor: tealMain },
              "& .MuiSelect-icon": { color: secondaryTextColor },
            }}
          >
            {roleOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* 3. USER ROSTER DATA TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "620px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "100px" }}>User ID</TableCell>
                <TableCell>Personnel Name</TableCell>
                <TableCell>Assigned Role</TableCell>
                <TableCell>Billing NPI</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No Personnel Found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const roleBadge = getRoleBadge(row.role);

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          transition: "background-color 0.15s ease",
                          "&:hover": { backgroundColor: rowHoverBg },
                          "& td": {
                            borderBottom: `1px solid ${
                              isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                            }`,
                            py: 1.4,
                          },
                        }}
                      >
                        {/* ID Badge */}
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: "6px",
                              backgroundColor: pastelTealBg,
                              color: tealButton,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                            }}
                          >
                            {row.id}
                          </Box>
                        </TableCell>

                        {/* Name with Avatar */}
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                backgroundColor: tealButton,
                                color: "#FFFFFF",
                              }}
                            >
                              {row.name[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.85rem" }}>
                                {row.name}
                              </Typography>
                              <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor, fontWeight: 500 }}>
                                {row.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                          <Chip
                            label={row.role}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              backgroundColor: roleBadge.bg,
                              color: roleBadge.color,
                            }}
                          />
                        </TableCell>

                        {/* NPI */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: primaryTextColor,
                            }}
                          >
                            {row.npi}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              backgroundColor:
                                row.status === "Active"
                                  ? isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7"
                                  : isDark ? "rgba(239, 68, 68, 0.15)" : "#FEE2E2",
                              color:
                                row.status === "Active"
                                  ? isDark ? "#34D399" : "#15803D"
                                  : isDark ? "#F87171" : "#B91C1C",
                            }}
                          />
                        </TableCell>

                        {/* Last Activity */}
                        <TableCell sx={{ color: secondaryTextColor, fontSize: "0.8rem" }}>
                          {row.lastLogin}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right" sx={{ pr: 2.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3 }}>
                            <Tooltip title="View Practitioner Profile">
                              <IconButton
                                size="small"
                                sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}
                              >
                                <VisibilityOutlinedIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit Permissions">
                              <IconButton
                                size="small"
                                sx={{ color: secondaryTextColor, "&:hover": { color: tealButton } }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Revoke User Access">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.id)}
                                sx={{
                                  color: "#EF4444",
                                  "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
                                }}
                              >
                                <DeleteOutlineRoundedIcon sx={{ fontSize: "1.05rem" }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>

      {/* 4. ADD USER MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Enroll Clinical Personnel
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Full Practitioner Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Dr. Arthur Pendelton"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Official Email Address
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="e.g. arthur.p@neuroez.com"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Billing NPI (If Applicable)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="10-digit NPI"
                value={newUserData.npi}
                onChange={(e) => setNewUserData({ ...newUserData, npi: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Clinical / Administrative Role
            </Typography>
            <Select
              fullWidth
              size="small"
              value={newUserData.role}
              onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              sx={{
                borderRadius: "6px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="Lead Neurologist">Lead Neurologist</MenuItem>
              <MenuItem value="Neuropsychiatrist">Neuropsychiatrist</MenuItem>
              <MenuItem value="Registered Nurse (RN)">Registered Nurse (RN)</MenuItem>
              <MenuItem value="Physical Therapist">Physical Therapist</MenuItem>
              <MenuItem value="Billing Specialist">Billing Specialist</MenuItem>
              <MenuItem value="Super Admin">Super Admin</MenuItem>
            </Select>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Enroll User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;